import React, { FC, useState, useEffect } from 'react';
import { css } from 'styled-components';
import { useKeycloak } from '@react-keycloak/web';
import { useController, useForm } from 'react-hook-form';

import Field from 'saur_ui_library/src/Molecules/Field/Field';
import Container from 'saur_ui_library/src/Atoms/Container/Container';
import Loader from 'saur_ui_library/src/Atoms/Loader/Loader';
import Form from 'saur_ui_library/src/Atoms/Input/Form/Form';
import Button from 'saur_ui_library/src/Atoms/Input/Button/Button';
import Word from 'saur_ui_library/src/Atoms/Word/Word';

// CPOSM
import loginFormConfig from 'saur-viseau-state-management/lib/settings/forms/loginForm';

//INTERNAL
import TestIds from '../../../common/Constants/TestIds';
import { KeyCloakProvider } from '../../../common/authProvider';
import { LocalStorage } from '../../../../../utils/localStorage';

interface IProps {
  realm: string;
  clientIdIntern: string;
  idpUrl: string;
  titleLabel: string;
  loginBtnLabel: string;
  inputTitleLabel: string;
  inputPlaceholderLabel: string;
}

const LoginViewV2: FC<IProps> = ({
  realm,
  clientIdIntern,
  idpUrl,
  titleLabel = 'Connexion',
  loginBtnLabel = 'Me connecter',
  inputTitleLabel = 'Identifiant',
  inputPlaceholderLabel = 'Votre email',
}) => {
  const { keycloak } = useKeycloak();
  const [loading, setLoading] = useState(false);
  const [isEmployeee, setIsEmployee] = useState(false);
  const providerLocalstorageKey = 'keycloak_provider';
  const userNameLocalstorageKey = 'username';
  const saurExtensions = ['@saur.com', '@groupesaur.fr'];

  //handle form functions
  const { fieldNames, validate, placeholders } = loginFormConfig();

  /**
   * Change the provider in the storage and reload the page
   */
  const switchProviderToIntern = () => {
    const newProvider: KeyCloakProvider = {
      url: idpUrl ?? '',
      realm: realm ?? '',
      clientId: clientIdIntern ?? '',
    };

    localStorage.setItem(providerLocalstorageKey, JSON.stringify(newProvider));
    localStorage.setItem(userNameLocalstorageKey, value.trim());
    window.location.reload();
  };

  /**
   * Lance l'authentification pour un utilisateur interne
   * Fonction appelée automatiquement
   */
  const fireLogin = () => {
    const userEmail = value;
    keycloak.login({
      scope: 'openid',
      loginHint: userEmail,
    });

    localStorage.removeItem(userNameLocalstorageKey);
  };

  const { control, handleSubmit } = useForm();
  const onSubmit = () => {};

  const {
    field: { value = fieldNames.email, onChange },
    fieldState: { error },
  } = useController({
    name: 'email',
    control,
    defaultValue: '',
    rules: {
      validate: validate,
    },
  });

  /**
   * Vérifie l'email de l'utilisateur et lance l'authentification si un utilisateur collectivité
   * Sinon on switch de provider et on recharge la page.
   */
  const handleAuth = () => {
    const userEmail = value;
    LocalStorage.userMail.set(userEmail);

    if (!error) {
      setLoading(true);
      // some function just like find function, but returns a boolean instead
      const isEmployee = saurExtensions.some((emailExtension: string) =>
        userEmail.trim().endsWith(emailExtension),
      );

      if (!isEmployee) {
        keycloak.login({
          scope: 'openid',
          loginHint: userEmail,
        });
      } else {
        // switch provider to intern and reload
        switchProviderToIntern();
      }
    }
  };

  useEffect(() => {
    if (isEmployeee) {
      /**
       * Obligé de passer par un onClick sur le bouton car sans cela
       * on obtient une erreur de type "Auth Error"
       */
      const btn = document.querySelector(
        '#hiddenBtnTestId',
      ) as HTMLElement | null;
      if (btn) {
        setTimeout(() => {
          btn.click();
        }, 100);
      }
    }
  }, [isEmployeee]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // if the provider exist => auto login auto and clear local storage
    const provider = localStorage.getItem(providerLocalstorageKey);
    const storedUserName = localStorage.getItem(userNameLocalstorageKey);

    localStorage.removeItem(userNameLocalstorageKey);

    if (provider !== null && storedUserName !== null) {
      if (JSON.parse(provider).clientId === clientIdIntern) {
        setIsEmployee(true);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Container className='login-V2-container'>
      <Word textStyle='title' className='title' value={titleLabel} />
      {loading ? (
        <Container className='login-screen-loading'>
          <Loader />
        </Container>
      ) : (
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Field
            placeholder={placeholders.email}
            value={value}
            label={inputTitleLabel}
            onChange={onChange}
            errorMessage={error?.message ? error?.message : null}
          />
          <Button
            type='submit'
            mode='outline'
            automatedTestId={TestIds.Login.confirmLoginButton}
            onClick={handleAuth}
          >
            <Word value={loginBtnLabel} textStyle='normalBold' />
          </Button>
          {isEmployeee && (
            <Button
              type='button'
              mode='outline'
              automatedTestId={'hiddenBtnTestId'}
              onClick={fireLogin}
              className={'hidden-btn'}
            >
              <Word
                value={'Me connecter en tant que collaborateur SAUR'}
                textStyle='normalBold'
              />
            </Button>
          )}
        </Form>
      )}
    </Container>
  );
};

export default LoginViewV2;

export const LoginViewV2Styles = css`
  .login-V2-container {
    background-color: white;
    border-radius: 15px;
    box-shadow: rgb(16 26 49 / 10%) 0px 14px 14px 3px;
    overflow: hidden;
    position: relative;
    padding: 50px;

    .login-screen-loading {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 200px;
    }
    .title {
      font-size: 32px;
      font-family: montserrat-bold;
      text-align: center;
    }

    .sds-button-container {
      margin: 0 auto;
    }

    .hidden-btn {
      display: none;
    }
  }
`;
